/**
 * Offset applied when an anchor is scrolled to, so the target heading lands below the
 * page header instead of under it. Also the scroll-spy's "a heading counts as active
 * once it reaches here" line, so the highlighted table-of-contents row matches the
 * section at the top of the reader's view.
 *
 * It lives in its own module because both the renderer and the table of contents need
 * it, and the renderer is lazily loaded. Importing the constant from `DocContent` would
 * be a static edge into the lazy chunk, which pulls the whole markdown renderer back
 * into the eager bundle and quietly undoes the split (rollup says so out loud, which is
 * how this was caught).
 */
export const ANCHOR_OFFSET = 96

/**
 * Height of the header band each of the three columns opens with: the search field on the
 * left, the document title in the middle, the "On this page" label on the right.
 *
 * Every column centres its first element inside a band of this height, so the three line
 * up BY CONSTRUCTION rather than by per-column padding tuned against one font size. The
 * hand-tuned version drifts as soon as a title wraps to two lines, and it cannot be right
 * for both at once anyway: a 34px title and a 34px-tall field have different box heights
 * and different optical centres, so one number cannot align them.
 *
 * 48px, because the shared search field measures 49px at its ROOT — not the 34px an
 * earlier version of this constant took from the inner `<input>`. At 40 the field overhung
 * the band by ~4.5px, putting the topmost pixel of the page above the 28.4px line every
 * other page's title starts on. At 48 nothing overhangs and the field's top edge lands on
 * that line, while the title's 41px line box still sits inside without clipping.
 */
export const DOCS_HEADER_HEIGHT = 48

/**
 * Top inset for the whole docs slab, so its header band starts where every other page's
 * title starts.
 *
 * 6.25 on the 4px theme base = 25px. Re-derived: this was `1` (4px), calibrated against a
 * then-correct measurement of the shared `Header` putting a title top at 28.4px. That number
 * has since changed — `Header` now reserves the wayfinding row's space and a 49px title row
 * unconditionally, so every platform page puts its title at 53px. Docs measured 32px against
 * that, hence +21px here.
 *
 * This constant is calibrated against `Header`, so it does NOT track it automatically: if the
 * header's top block changes again, re-measure a platform page's title top and adjust. The
 * docs slab is a different layout with a 34px title, so it cannot simply import the value.
 *
 * DOCS_STICKY_TOP is derived from this, so moving it also moves where the two rails pin — which
 * is the point: they must pin exactly where they are painted. Do not "fix" a rail offset by
 * editing that constant; fix it here, or the rails jump on first scroll.
 */
export const DOCS_SLAB_TOP = 6.25

/**
 * `top` for the two sticky rails (section nav, table of contents), in PIXELS.
 *
 * They used to say `top: 0`, which is not "sticky from the start": the scrolling
 * ancestor is an inner `overflow-y: auto` box whose padding puts both columns 28px below
 * its own top edge, so each one drifted UP those 28px before pinning. Small, but it reads
 * as the sidebars lurching on first scroll while the prose moves normally.
 *
 * It is DOCS_SLAB_TOP, and ONLY that — so it is DERIVED, not restated. `top` on a sticky
 * element is measured from the scroll container's PADDING BOX, not from the viewport, so the
 * container's own 24px of top padding is already accounted for and must not be added here.
 * Adding it (top: slab + 24) does not merely fail to help: it then exceeds the element's
 * natural offset inside that padding box, so sticky pushes the column DOWN on first paint,
 * which knocked the search field below the document title it is supposed to align with.
 *
 * Derived because a hand-copied number here goes stale silently and the symptom does not name
 * its cause. This was literally 4 while DOCS_SLAB_TOP had moved to 25, and both rails lurched
 * 21px UP on the first scroll — pinning 21px above where they were painted — while the prose
 * scrolled normally. Nothing warns you: the constant is still internally consistent, just no
 * longer consistent with the thing it is a function OF. `* 4` converts the slab's theme
 * spacing factor to px (the `@repo/ui` base is 4px, NOT MUI's default 8).
 */
export const DOCS_STICKY_TOP = DOCS_SLAB_TOP * 4

/**
 * Extra top offset for the RIGHT column only, in pixels.
 *
 * The three columns were deliberately made to start on one line, and for the search field
 * and the document title that is right — they are both the head of their column. The
 * contents list is not: it is an aside ABOUT the document, and putting its label level with
 * the title made the page read as three equal columns rather than a document with two
 * rails. Dropping it clear of the title line is what distinguishes them.
 *
 * Applied to the initial offset AND to the sticky `top`, so the list pins where it starts
 * rather than jumping up to the title's line on first scroll.
 */
export const DOCS_TOC_TOP_OFFSET = 28

/**
 * Cap for the whole three-column slab, centred.
 *
 * Moved here from DocPage when DocsLayout was extracted — the layout owns the geometry now, and a
 * width constant that lives in one of two consumers is a constant waiting to disagree.
 *
 * At full width the two rails were flung to the screen edges with the prose stranded between them
 * and the page stopped reading as a document. Inert below the max: at 1280 nothing moves.
 */
export const DOCS_MAX_WIDTH = 1500
